import React, { useEffect, useState } from 'react';
import { Eye, Trash, Check, X } from '@phosphor-icons/react';
import axios from 'axios';
import { API } from '../../App';

const Contacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API}/admin/contacts`, {
        withCredentials: true
      });
      setContacts(response.data);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/contacts/${id}/status?status=${status}`, {}, {
        withCredentials: true
      });
      setContacts(contacts.map(c => c.id === id ? { ...c, status } : c));
      if (selectedContact?.id === id) {
        setSelectedContact({ ...selectedContact, status });
      }
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const deleteContact = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette demande ?')) return;

    try {
      await axios.delete(`${API}/admin/contacts/${id}`, {
        withCredentials: true
      });
      setContacts(contacts.filter(c => c.id !== id));
      if (selectedContact?.id === id) {
        setSelectedContact(null);
      }
    } catch (error) {
      console.error('Failed to delete contact:', error);
    }
  };

  const filteredContacts = contacts.filter(contact => {
    if (filter === 'all') return true;
    return contact.status === filter;
  });

  const statusColors = {
    new: 'bg-[#C5A059]/20 text-[#C5A059]',
    read: 'bg-blue-500/20 text-blue-400',
    replied: 'bg-green-500/20 text-green-400',
    archived: 'bg-stone-700/50 text-stone-400'
  };

  const statusLabels = {
    new: 'Nouveau',
    read: 'Lu',
    replied: 'Répondu',
    archived: 'Archivé'
  };

  return (
    <div data-testid="admin-contacts">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-display text-3xl text-white mb-2">Demandes de Contact</h1>
        <p className="text-stone-400">Gérez les demandes reçues via le formulaire</p>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {['all', 'new', 'read', 'replied', 'archived'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 text-sm rounded transition-colors whitespace-nowrap ${
              filter === f
                ? 'bg-[#C5A059] text-black'
                : 'bg-[#121212] text-stone-400 hover:text-white'
            }`}
          >
            {f === 'all' ? 'Tous' : statusLabels[f]}
            {f === 'new' && contacts.filter(c => c.status === 'new').length > 0 && (
              <span className="ml-2 bg-white/20 px-1.5 py-0.5 rounded text-xs">
                {contacts.filter(c => c.status === 'new').length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="spinner" />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* List */}
          <div className="admin-card divide-y divide-white/5 max-h-[70vh] overflow-y-auto">
            {filteredContacts.length === 0 ? (
              <div className="p-8 text-center text-stone-400">
                Aucune demande {filter !== 'all' ? `avec le statut "${statusLabels[filter]}"` : ''}
              </div>
            ) : (
              filteredContacts.map((contact) => (
                <div
                  key={contact.id}
                  onClick={() => {
                    setSelectedContact(contact);
                    if (contact.status === 'new') {
                      updateStatus(contact.id, 'read');
                    }
                  }}
                  className={`p-4 cursor-pointer transition-colors ${
                    selectedContact?.id === contact.id
                      ? 'bg-white/5'
                      : 'hover:bg-white/5'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="text-white font-medium">{contact.name}</p>
                      <p className="text-sm text-stone-400">{contact.email}</p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded ${statusColors[contact.status]}`}>
                      {statusLabels[contact.status]}
                    </span>
                  </div>
                  <p className="text-sm text-stone-500 line-clamp-2">{contact.message}</p>
                  <p className="text-xs text-stone-600 mt-2">
                    {new Date(contact.created_at).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              ))
            )}
          </div>

          {/* Detail View */}
          <div className="admin-card">
            {selectedContact ? (
              <div className="p-6">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h2 className="font-display text-2xl text-white">{selectedContact.name}</h2>
                    <a 
                      href={`mailto:${selectedContact.email}`}
                      className="text-[#C5A059] hover:underline"
                    >
                      {selectedContact.email}
                    </a>
                  </div>
                  <button
                    onClick={() => setSelectedContact(null)}
                    className="text-stone-400 hover:text-white"
                  >
                    <X size={24} weight="light" />
                  </button>
                </div>

                <div className="space-y-4 mb-8">
                  {selectedContact.company && (
                    <div>
                      <p className="text-xs text-stone-500 uppercase tracking-wider mb-1">Entreprise</p>
                      <p className="text-stone-300">{selectedContact.company}</p>
                    </div>
                  )}
                  {selectedContact.phone && (
                    <div>
                      <p className="text-xs text-stone-500 uppercase tracking-wider mb-1">Téléphone</p>
                      <a href={`tel:${selectedContact.phone}`} className="text-stone-300 hover:text-[#C5A059]">
                        {selectedContact.phone}
                      </a>
                    </div>
                  )}
                  {selectedContact.event_type && (
                    <div>
                      <p className="text-xs text-stone-500 uppercase tracking-wider mb-1">Type d'événement</p>
                      <p className="text-stone-300">{selectedContact.event_type}</p>
                    </div>
                  )}
                  {selectedContact.event_date && (
                    <div>
                      <p className="text-xs text-stone-500 uppercase tracking-wider mb-1">Date de l'événement</p>
                      <p className="text-stone-300">{selectedContact.event_date}</p>
                    </div>
                  )}
                  {selectedContact.event_location && (
                    <div>
                      <p className="text-xs text-stone-500 uppercase tracking-wider mb-1">Lieu</p>
                      <p className="text-stone-300">{selectedContact.event_location}</p>
                    </div>
                  )}
                  {selectedContact.guest_count && (
                    <div>
                      <p className="text-xs text-stone-500 uppercase tracking-wider mb-1">Nombre d'invités</p>
                      <p className="text-stone-300">{selectedContact.guest_count}</p>
                    </div>
                  )}
                </div>

                <div className="mb-8">
                  <p className="text-xs text-stone-500 uppercase tracking-wider mb-2">Message</p>
                  <p className="text-stone-300 whitespace-pre-wrap">{selectedContact.message}</p>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-2 pt-6 border-t border-white/5">
                  <button
                    onClick={() => updateStatus(selectedContact.id, 'replied')}
                    className="btn-primary text-sm py-2 flex items-center gap-2"
                  >
                    <Check size={16} weight="bold" />
                    Marquer comme répondu
                  </button>
                  <button
                    onClick={() => updateStatus(selectedContact.id, 'archived')}
                    className="btn-secondary text-sm py-2"
                  >
                    Archiver
                  </button>
                  <button
                    onClick={() => deleteContact(selectedContact.id)}
                    className="px-4 py-2 text-sm text-red-400 hover:text-red-300 transition-colors"
                  >
                    <Trash size={16} weight="light" />
                  </button>
                </div>
              </div>
            ) : (
              <div className="h-full flex items-center justify-center p-12 text-center">
                <div>
                  <Eye size={48} weight="thin" className="text-stone-600 mx-auto mb-4" />
                  <p className="text-stone-400">
                    Sélectionnez une demande pour voir les détails
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Contacts;
